import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

// Only load default language synchronously - others load on demand
import enTranslations from './locales/en.json'
import { debug } from '../utils/debug'

// Supported languages with their Vite dynamic import loaders
const localeLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  hi: () => import('./locales/hi.json'),
}

// Track which languages have been loaded
const loadedLanguages = new Set<string>(['en'])

/**
 * Lazily load a language's translations using Vite dynamic imports.
 * Returns immediately if language is already loaded.
 */
export async function loadLanguage(lang: string): Promise<void> {
  if (loadedLanguages.has(lang)) {
    return
  }

  const loader = localeLoaders[lang]
  if (!loader) {
    if (import.meta.env.DEV) {
      debug.error(`[i18n] No loader found for language: ${lang}`)
    }
    return
  }

  const translations = await loader()
  i18n.addResourceBundle(lang, 'translation', translations.default, true, true)
  loadedLanguages.add(lang)
}

/**
 * Change language with lazy loading support.
 * Loads the language bundle if not already loaded, then switches.
 */
export async function changeLanguage(lang: string): Promise<void> {
  await loadLanguage(lang)
  await i18n.changeLanguage(lang)
}

// Initialize with only English loaded (reduces initial bundle)
void i18n.use(initReactI18next).init({
  resources: {
    en: {
      translation: enTranslations,
    },
  },
  lng: 'en',
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false, // React already escapes by default
  },
  debug: import.meta.env.DEV,
})

export default i18n
